import React, {
    useState,
    useEffect,
    useRef,
    useCallback,
    forwardRef,
    createRef,
} from 'react';
import PropTypes from 'prop-types';
import { isEqual as _isEqual } from 'lodash';
import {
    isEmail,
    isURL,
    isIP,
    isIPRange,
    isPort,
    isLatLong,
    isHexadecimal,
} from 'validator';

import TextField from '@mui/material/TextField';

import Helpers from '../libraries/helpers';

const typeChecks = {
    password: v => {
        return /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&\-\+\_\#\^\.\,\;])[A-Za-z\d@$!%*?&\-\+\_\#\^\.\,\;]{8,}$/.test(v);
    },
    email: v => isEmail(v) && v.length < 255,
    hex: v => isHexadecimal(v),
    supportKey: v => isHexadecimal(v) && v.length == 20,
    host: v => {
        return (isURL(v, {
            require_protocol: false,
            require_tld: false,
            require_host: true
        }) && length < 255) || isIP(v, 4) || isIP(v, 6);
    },
    url: v => isURL(v, { require_protocol: true }),
    ipv4: v => isIP(v, 4),
    ipv6: v => isIP(v, 6),
    ip: v => (isIP(v, 4) || isIP(v, 6)),
    ipRange: v => (isIPRange(v) || isIP(v, 4) || isIP(v, 6)),
    ipList: v => {
        let i;
        let valid = true;
        const list = v.split(',');

        for (i = 0; i < list.length; i++) {
            if (!isURL(list[i].trim(), { require_protocol: false, require_tld: false, require_host: true })) {
                valid = false;
                break;
            }
        }

        return valid;
    },
    cidr: v => (isIPRange(v)),
    port: v => isPort(String(v)),
    latlon: v => isLatLong(v),
    sensorName: v => /^\w[a-zA-Z0-9_\-. (){}\[\]]{0,63}$/.test(v) && v.length < 64,
    policyName: v => /^[\w\u0041-\uffff](\w| |_|-|[\u0041-\uffff]){0,17}$/.test(v) && v.length < 18,
    exportName: v => /^\w[a-zA-Z0-9_\-. ]{0,31}$/.test(v) && v.length < 32,
    filterName: v => /^\w[a-zA-Z0-9_\-. (){}\[\]]{0,44}$/.test(v) && v.length < 45,
    snmpName: v => /^\w[a-zA-Z0-9_\-.]{1,64}$/.test(v) && v.length < 64,
    validNameCharacter: v => /^[\w\-\s]+$/.test(v) && v.length < 45,
    username: v => v.length < 32,
    displayName: v => v.length < 32,
    assetName: v => v.length > 0 && v.length < 42,
    kafkaTopicName: v => /^\w[a-zA-Z0-9_\-.]{0,255}$/.test(v),
    range: (v, min, max) => (parseInt(v) >= min && parseInt(v) <= max),
    length: (v, min) => (v.length >= min),
    // floating point range -1:1 e.g. "-0.3:0.3"
    pcr: v => {
        const vs = (v || '').split(':');
        if (vs.length !== 2) return false;
        const l = parseFloat(vs[0].trim());
        if (isNaN(l) || l < -1 || l > 1) return false;
        const r = parseFloat(vs[1].trim());
        if (isNaN(r) || r < -1 || r > 1) return false;
        return l <= r; // not sure about equals here
    },
    index: v => /^[a-z0-9]([a-z0-9_\-]{0,254}|)$/.test(v) && v.length < 255,
    portRange: v => {
        const portRange0to65535 = '([0-9]{1,4}|[1-5][0-9]{4}|6[0-4][0-9]{3}|65[0-4][0-9]{2}|655[0-2][0-9]|6553[0-5])';
        const portOptionalRangeOfRange = '\\s*' + portRange0to65535 + '(\\s*-\\s*' + portRange0to65535 + '\\s*)?';
        const portListOfRanges = '\\s*' + portOptionalRangeOfRange + '(\\s*,\\s*' + portOptionalRangeOfRange + '\\s*){0,}';
        const portRangeRegex = new RegExp('^' + portListOfRanges + '$');
        return portRangeRegex.test(v);
    },
    keyPairList: v => {
        v = Object.prototype.toString.call(v).includes('Array') ? v : v.split(',');
        return v.map(x => {
            const r = /^[a-zA-Z][a-zA-Z0-9\.-_]*[a-zA-Z0-9]\:[a-zA-Z][a-zA-Z0-9\.-_]*[a-zA-Z0-9]$/;
            const t = x.match(r);

            return !!t;
        }).reduce((a, c) => {
            if (!a || !c) {
                return false;
            } else {
                return true;
            }
        }, true);
    },
    serial: v => /^([A-Za-z]{4}-){5}[A-Za-z]{4}$/.test(v) && v.length < 30,
    mac: v => /^([A-Fa-f0-9]{2}:){5}[A-Fa-f0-9]{2}$/.test(v) && v.length < 18,
    kafkaSslCipherSuites: v => /^([A-Za-z0-9!+\-_@=]+)(:[A-Za-z0-9!+\-_@=]+)*$/.test(v) && v.length > 0 && v.length < 1024,
};

const useNoisyValidate = (init) => {
    const [validate, setValidate] = useState(init);

    const set = useCallback(value => {
        setValidate(value);
        return value;
    }, []);

    const get = useCallback(() => validate, [validate]);

    return { get, set };
};

const useValidate = (init) => {
    const [validate, setValidate] = useState(init);
    const [ret, setRet] = useState({ get: () => {}, set: () => {} });

    const set = useCallback((value) => {
        setValidate(value);
        return value;
    }, []);

    const get = useCallback(() => validate, [validate]);

    // noise reduction
    useEffect(() => {
        setRet(current => {
            current.get = get;
            current.set = set;
            current.kicker = 0;
            return current;
        });
    }, [set, get]);

    useEffect(() => {
        // because 'ret' is an object with two callback (cached) functions
        // watchers may not be notified when 'validate' value changes
        // solution: watchers can add 'ret.kicker' to their dependency list
        setRet(current => {
            current.kicker = Math.random();
            return current;
        });
    }, [validate]);

    return ret;
};

const Validator = forwardRef((props, ref) => {
    const {
        allowEmpty,
        valueType,
        onChange,
        onRemoval,
        helperText,
        onValidate,
        errorText,
        endAdornment,
        unique,
        component,
        kick,
        equal,
        resetField,
        ...rest
    } = props;
    const textfieldRef = createRef();
    const [id] = useState(Helpers.UniqueID());
    const [isValid, setIsValid] = useState(false);
    const [init, setInit] = useState(true);

    useEffect(() => {
        if (resetField) {
            setIsValid(false);
            setInit(true);
        }
    }, [resetField]);

    // incoming
    useEffect(() => {
        let v = false;
        const notRequired = allowEmpty || props.disabled;

        if ((!props.value && allowEmpty) || props.disabled) {
            v = true;
        } else if (props.value != null) {
            if (props.valueType) {
                if (props.valueType === 'range') {
                    v = typeChecks[props.valueType](props.value, props.inputProps.min, props.inputProps.max);
                } else if (props.valueType === 'length') {
                    v = typeChecks[props.valueType](props.value, props.inputProps.min);
                } else if (typeChecks[props.valueType](props.value)) {
                    v = true;
                }
            } else if (!props.valueType && props.value && !notRequired) {
                v = true;
            } else if (!props.value && notRequired) {
                v = true;
            }
        }

        if (v && unique) {
            if (unique.indexOf(props.value) !== unique.lastIndexOf(props.value)) {
                v = false;
            }
            // for(let i=unique.length;i--;) {
            //     if(props.value === unique[i]) {
            //         v = false;
            //         break;
            //     }
            // }
        }

        if (v && props.value && equal) {
            v = _isEqual(props.value, equal);
        }

        setIsValid(v);
        onChange({ target: textfieldRef.current }, id, v, true, props.value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.value, kick]);

    useEffect(() => {
        return () => {
            if (onRemoval) {
                onRemoval(id);
            }
        };
    }, []);

    // outgoing
    const handleChange = (e) => {
        let v = false;
        const notRequired = allowEmpty || props.disabled;

        if ((!props.value && allowEmpty) || props.disabled) {
            v = true;
        } else if (props.valueType) {
            if (props.valueType === 'range') {
                v = typeChecks[props.valueType](e.target.value, props.inputProps.min, props.inputProps.max);
            } else if (typeChecks[props.valueType](e.target.value)) {
                v = true;
            }
        } else if (!props.valueType && e.target.value && !notRequired) {
            v = true;
        } else if (!e.target.value && notRequired) {
            v = true;
        }

        if (v && e.target.value && unique) {
            if (unique.indexOf(e.target.value) !== unique.lastIndexOf(e.target.value)) {
                v = false;
            }
        }

        if (v && e.target.value && equal) {
            v = _isEqual(e.target.value, equal);
        }

        if (init) setInit(false);
        setIsValid(v);
        // GLM: added ducktape - the last two params:
        // false = value is outgoing (caller needs to update model)
        // value = textfieldRef.current doesn't hold updated value for selection list
        onChange({ target: textfieldRef.current }, id, v, false, e.target.value);
    };

    return (component
        ? React.createElement(component, {
            ...rest,
            onChange: handleChange,
            error: !init && !isValid,
        })
        : <TextField
            {...rest}
            onChange={handleChange}
            error={!init && !isValid}
            helperText={(errorText && !isValid && !init) ? errorText : ''}
            inputRef={(input) => { textfieldRef.current = input; if (ref !== null) { ref.current = input } }}
            InputProps={{
                endAdornment: endAdornment,
            }}
        />
    );
});

const checkValidity = (state, allowEmpty) => {
    if (allowEmpty) {
        if (!state) return false;
    } else {
        if (!state || Object.keys(state).length === 0) return false;
    }

    const k = Object.keys(state);
    for (let i = k.length; i--;) {
        if (!state[k[i]]) {
            return false;
        }
    }

    return true;
};

Validator.propTypes = {
    allowEmpty: PropTypes.bool,
    multiple: PropTypes.bool,
    valueType: PropTypes.oneOf([
        'password',
        'email',
        'hex',
        'host',
        'url',
        'ipv4',
        'ipv6',
        'ip',
        'ipRange',
        'ipList',
        'cidr',
        'port',
        'portRange',
        'latlon',
        'sensorName',
        'policyName',
        'exportName',
        'filterName',
        'snmpName',
        'validNameCharacter',
        'username',
        'displayName',
        'assetName',
        'range',
        'length',
        'md5url',
        'pcr',
        'kafkaTopicName',
        'supportKey',
        'index',
        'keyPairList',
        'serial',
        'mac',
        'kafkaSslCipherSuites',
    ]),
    resetField: PropTypes.bool,
    errorText: PropTypes.string,
    unique: PropTypes.array,
    equal: PropTypes.any,
    autoComplete: PropTypes.string,
    autoFocus: PropTypes.bool,
    classes: PropTypes.object,
    color: PropTypes.oneOf(['primary', 'secondary']),
    defaultValue: PropTypes.any,
    disabled: PropTypes.bool,
    error: PropTypes.bool,
    FormHelperTextProps: PropTypes.object,
    fullWidth: PropTypes.bool,
    helperText: PropTypes.node,
    id: PropTypes.string,
    InputLabelProps: PropTypes.object,
    inputProps: PropTypes.object,
    InputProps: PropTypes.object,
    inputRef: PropTypes.func,
    label: PropTypes.node,
    margin: PropTypes.oneOf(['dense', 'none', 'normal']),
    multiline: PropTypes.bool,
    name: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    placeholder: PropTypes.string,
    required: PropTypes.bool,
    rows: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    rowsMax: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    select: PropTypes.bool,
    SelectProps: PropTypes.object,
    size: PropTypes.oneOf(['medium', 'small']),
    type: PropTypes.string,
    value: PropTypes.any,
    variant: PropTypes.oneOf(['filled', 'outlined', 'standard']),
    kick: PropTypes.any,
    component: PropTypes.elementType,
    onRemoval: PropTypes.func,
    onValidate: PropTypes.func,
    endAdornment: PropTypes.node,
};

export {
    Validator as default,
    typeChecks,
    useNoisyValidate,
    useValidate,
    checkValidity
};
